import { Card, Image, Text } from "@clipboard-health/ui-components";
import { CardContent, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useHistory } from "react-router-dom";

import { type PlacementCandidate } from "../../../PlacementCandidate/types";
import {
  transformPaginatedInterviews,
  useInterviewAttendanceOutcome,
} from "../../hooks/useInterviewAttendanceOutcome";
import { PLACEMENTS_BASE_PATH } from "../../paths";
import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

interface InterviewAttendanceFeedbackCardProps {
  placementCandidate: PlacementCandidate;
}

export function InterviewAttendanceFeedbackCard(props: InterviewAttendanceFeedbackCardProps) {
  const { placementCandidate } = props;
  const worker = useDefinedWorker();
  const history = useHistory();
  const source = useInterviewAttendanceSource();

  const { data: interviewsDataWithPages } = useInterviewAttendanceOutcome({
    workerId: worker.userId,
    withDismissed: false,
  });
  const interviews = transformPaginatedInterviews(interviewsDataWithPages?.pages, worker.tmz ?? "");

  const hasInterviews = interviews && interviews.length > 0;
  const hasMultipleInterviews = interviews && interviews.length > 1;

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_CARD_VIEWED,
    {
      placementCandidate,
      source,
    },
    { enabled: hasInterviews }
  );

  return hasInterviews ? (
    <Card
      sx={{
        p: 1.5,
        // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
        background: `linear-gradient(135deg, #FBF2E8 20%, #B7DDFF 100%)`,
      }}
    >
      <CardContent component={Stack} spacing={5} sx={{ alignItems: "center" }}>
        <Stack
          spacing={4}
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between", paddingX: 4 }}
        >
          <Stack spacing={6}>
            <Text semibold variant="h4">
              {hasMultipleInterviews
                ? "How did your recent interviews go?"
                : "How did your interview go?"}
            </Text>
            <Button
              invert
              variant="contained"
              sx={{ width: "fit-content" }}
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_SHARE_CLICKED, {
                  placementCandidate,
                  source,
                });
                history.push(`${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/interviews/outcome`);
              }}
            >
              {hasMultipleInterviews ? `Share ${interviews.length} outcomes` : "Share the outcome"}
            </Button>
          </Stack>
          <Image src="assets/images/chat.png" alt="Interview Outcome" width="30%" />
        </Stack>
      </CardContent>
    </Card>
  ) : undefined;
}
